import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================================================
// Schemas
// ============================================================================

const RebateExternalFigureCategorySchema = z.enum([
  "Redistribution",
  "Non-Conditional",
  "Conditional",
]);

const ReportLineTypeSchema = z.enum(["estimates", "predictions", "reported"]);

const BudgetComparisonCategoryMonthSchema = z.object({
  month: z.number().int().min(1).max(12),
  forecast: z.number().nullable(),
  budget: z.number().nullable(),
  delta: z.number().nullable(),
});

const BudgetComparisonCategorySchema = z.object({
  name: RebateExternalFigureCategorySchema,
  months: z.array(BudgetComparisonCategoryMonthSchema),
  totalForecast: z.number(),
  totalBudget: z.number().nullable(),
  totalDelta: z.number().nullable(),
});

const BudgetComparisonResponseSchema = z.object({
  year: z.number().int(),
  lineType: ReportLineTypeSchema,
  generatedAt: z.date(),
  hasBudgetData: z.boolean(),
  categories: z.array(BudgetComparisonCategorySchema),
  totals: z.object({
    months: z.array(BudgetComparisonCategoryMonthSchema),
    totalForecast: z.number(),
    totalBudget: z.number().nullable(),
    totalDelta: z.number().nullable(),
  }),
});

const BudgetUploadValidateResponseSchema = z.object({
  valid: z.boolean(),
  year: z.number().int().nullable(),
  categoriesFound: z.array(z.string()),
  monthsFound: z.array(z.number().int()),
  totalRecords: z.number().int(),
  warnings: z.array(z.string()),
});

const BudgetUploadResponseSchema = z.object({
  success: z.boolean(),
  year: z.number().int(),
  recordsInserted: z.number().int(),
  recordsDeleted: z.number().int(),
});

const AvailableBudgetYearsResponseSchema = z.object({
  years: z.array(z.number().int()),
});

// ============================================================================
// Contracts
// ============================================================================

const getReport = oc
  .input(
    z.object({
      year: z.number().int(),
      categoryFilters: z.array(z.string()).optional(),
      lineType: ReportLineTypeSchema.optional(),
    }),
  )
  .output(z.object({ data: BudgetComparisonResponseSchema }))
  .route({ method: "GET", path: "/budget-comparison" });

const validateUpload = oc
  .input(
    z.object({
      fileBase64: z.string().describe("Base64-encoded Excel file content"),
      fileName: z.string(),
    }),
  )
  .output(z.object({ data: BudgetUploadValidateResponseSchema }))
  .route({ method: "POST", path: "/budget-comparison/validate" });

const upload = oc
  .input(
    z.object({
      fileBase64: z.string().describe("Base64-encoded Excel file content"),
      fileName: z.string(),
      year: z
        .number()
        .int()
        .describe("Override year (if not detected from file)"),
    }),
  )
  .output(z.object({ data: BudgetUploadResponseSchema }))
  .route({ method: "POST", path: "/budget-comparison/upload" });

const getAvailableYears = oc
  .output(z.object({ data: AvailableBudgetYearsResponseSchema }))
  .route({ method: "GET", path: "/budget-comparison/years" });

export const budgetComparisonContracts = {
  getReport,
  validateUpload,
  upload,
  getAvailableYears,
} as const;

// Re-export schemas for use in handlers
export {
  BudgetComparisonResponseSchema,
  BudgetUploadValidateResponseSchema,
  BudgetUploadResponseSchema,
};
